import { useEffect, useRef, useState } from 'react';
import Lightbox from 'react-spring-lightbox';
import { useFormik, useFormikContext } from 'formik';
import { useMutation, useQuery } from 'react-query';
import { get, head, isEmpty, isEqual, omit } from 'lodash-es';
import { useParams } from 'react-router';

import { Loader, PrimaryButton } from 'modules/v2/common/components';
import { ROUTE_REFERRAL_MARKETING_GENERATED_MAGAZINE } from 'modules/v2/routes/constants';
import {
  createReferralMagazine,
  createSetupTicket,
  generateReferralMagazine,
  generateReferralMagazineAdmin,
  getGeneratedMagazineAdmin,
  getGeneratedMagazineMonth,
  getMagazine,
  getMagazineAdmin,
  getMagazinePreview,
  getMagazineStepsInfo,
  sendToPrint as sendMagazineToPrint,
  updateReferralMagazine,
  updateReferralMagazineAdmin,
} from 'modules/api';
import { commonFunctions, isAdminEditing } from 'modules/v2/utils';
import { notification } from 'modules/v2/common/utils';
import { trackUsage } from 'modules/v2/common/utils/trackUsage';
import {
  getRouteRmMagazinePreview,
  getRouteRmMagazineReport,
} from 'modules/v2/routes/admin/navigation';
import {
  replaceFinalPayload,
  replaceNewLineWithBR,
} from 'modules/v2/pages/ReferralMarketingMagazines/components/Steps/FrontInsideCover/utils';

import { Alert, Modal } from 'flowbite-react';
import Button from 'modules/v2/common/AtomicDesign/atoms/Button';
import AlertWarning from 'modules/v2/common/components/SvgIcon/AlertWarning';
import PublishBookIcon from 'modules/v2/common/components/SvgIcon/PublishBookIcon';
import StepCheckIcon from 'modules/v2/common/components/SvgIcon/StepCheckIcon';
import AlertSolidWarningIcon from 'modules/v2/common/components/SvgIcon/AlertSolidWarningIcon';
import CloseXIcon from 'modules/v2/common/components/SvgIcon/CloseXIcon';
import { InfoIconNew } from 'modules/v2/common/components/SvgIcon/InfoIconNew';

import {
  generatedMagzineRefetchInterval,
  monthsList,
  rmMagazineStatus,
  rmMagzineEditorSteps,
  stepHavingForms,
  stepsList,
} from '../../constants';
import {
  BackCoverStepPage,
  BackInsideCover,
  FrontCoverStepPage,
  FrontInsideCover,
  GenerateMagazineStepPage,
  MagazineStepPage,
} from '../Steps';

import S from '../../styles';
import { initialValues } from './initialFormState';
import { formKeywordList, magazineFormikStepsList } from './constant';
import { getFormattedValues } from './utils';
import magazineEditorValidationSchema from './validationSchema';
import { magazinePublishStrings } from './publishStrings';
import {
  firstGeneratedMagazine,
  generatedMagazineIdFrom,
  usePublishWhenGenerated,
} from './publishWhenGenerated';

// A copy of the saved personal info (`baseReplacers[0]`), or {} when none.
// `bookImage` is left out: older uploads also wrote it there, but it belongs
// to the back cover's dynamicFields, and keeping it would resend a stale image
// and carry it to the next issue.
const savedPersonalInfoFrom = (magazineData) =>
  omit({ ...get(magazineData, 'baseReplacers[0]') }, 'bookImage');

// DS answers 404 (magazine not found / generated magazine not found) when
// nothing exists yet, so a 404 is an answer, not a failure worth retrying.
const retryUnlessNotFound = (failureCount, error) =>
  error?.response?.status !== 404 && failureCount < 3;

const MagazineEditor = ({ history }) => {
  const { values: referralMagazineContext, setFieldValue: setFormikContext } = useFormikContext();
  const { current } = referralMagazineContext;

  const params = useParams();

  const [magazine, setMagazine] = useState({
    pdf: '',
    frontInsideCoverText: '',
    additionalInformation: '',
    isFormUpdated: false,
    previewPdf: '',
    maxCoverLetterLength: 1500,
  });
  const [showImageLightBox, setShowImageLightBox] = useState(false);
  const [imageLightBox, setImageLightBox] = useState(null);
  const [presentMagazineID, setPresentMagazineID] = useState('');
  const [refetchGeneratedMagazine, setRefetchGeneratedMagazine] = useState(false);
  const [currentMagazineStatus, setCurrentMagazineStatus] = useState('');
  // The banner copy depends on the status, so it waits for the first answer.
  const [isMagazineStatusKnown, setMagazineStatusKnown] = useState(false);
  // True when the status could not be read (not a 404) even after retries.
  const [isMagazineStatusError, setMagazineStatusError] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isPublishedError, setPublishedError] = useState(false);
  // The index of the step that failed validation; -1 when none did.
  const [stepError, setStepError] = useState(-1);
  const [stepSucceed, setStepSucceed] = useState([]);
  const [previousMagazineData, setPreviousMagazineData] = useState({});

  const savedMessage = isAdminEditing()
    ? 'Magazine changes saved!'
    : 'My magazine changes have been saved';

  const redirectUserToNextStep = (magazineData) => {
    const { baseReplacers = {}, selections = [] } = magazineData;
    let nextStep = 0;

    if (baseReplacers?.length) {
      nextStep = 1;
    }
    if (selections.length) {
      const presentData = [...selections.filter((stepData) => stepData.page)];
      const lastIndex = presentData.length - 1;
      nextStep = presentData[lastIndex].page + 1;
    }

    if (isAdminEditing()) nextStep = 0;

    return setFormikContext('current', nextStep);
  };

  const { data: magazinePreviewData } = useQuery(['getMagazinePreview', params.previewId], () =>
    getMagazinePreview(params.previewId),
  );

  const { customerInfo, url: previewUrl } = get(magazinePreviewData, 'data', {});
  const { firstName, lastName } = head(customerInfo) || {};
  const fullName = `${firstName} ${lastName}`;

  // Mutation to generate magazine in last step.
  const { mutate: generateMagazine } = useMutation(
    (payload) =>
      isAdminEditing() ? generateReferralMagazineAdmin(payload) : generateReferralMagazine(payload),
    {
      onSuccess: () => {
        /*  eslint-disable no-use-before-define */
        trackUsage('magazine', 'generate', { isAdminEditing: isAdminEditing() });
        removeGeneratedMagazineInfo();
        setRefetchGeneratedMagazine(generatedMagzineRefetchInterval);
        notification.success({
          description: savedMessage,
          autoClose: 4000,
        });
      },
      onError: () => {
        notification.error({ description: 'Something went wrong', autoClose: 4000 });
      },
    },
  );

  // Update the referral magazine
  const { mutateAsync: sendToPrinting } = useMutation(
    (payload) => (isAdminEditing() ? createSetupTicket(payload) : sendMagazineToPrint(payload)),
    {
      onSuccess: (data) => {
        trackUsage('magazine', 'send_to_print', {
          isAdminEditing: isAdminEditing(),
          status: get(data, 'status'),
        });
        setCurrentMagazineStatus(get(data, 'status'));
      },
      onError: () => {
        notification.error({
          description: 'Something went wrong',
          autoClose: 4000,
        });
      },
    },
  );

  // get the generated magazine and update the additional Information
  let getGeneratedMagazineQueryKey = ['getGeneratedMagazineMonth', params.year, params.month];
  let getGeneratedMagazineFn = getGeneratedMagazineMonth;

  if (isAdminEditing()) {
    getGeneratedMagazineQueryKey = [
      'getGeneratedMagazineAdmin',
      params.year,
      params.month,
      params.customerId,
    ];
    getGeneratedMagazineFn = getGeneratedMagazineAdmin;
  }

  const {
    data: toFormat = {},
    remove: removeGeneratedMagazineInfo,
    refetch: fetchGeneratedMagazine,
  } = useQuery(getGeneratedMagazineQueryKey, getGeneratedMagazineFn, {
    refetchInterval: refetchGeneratedMagazine,
    retry: retryUnlessNotFound,
    onSuccess: (generatedMagazineData) => {
      const updatedMagazineData = {
        ...magazine,
        additionalInformation: get(generatedMagazineData, 'additionalInformation') || '',
        bookUrl: get(generatedMagazineData, 'bookUrl') || '',
        pageUrl: get(generatedMagazineData, 'pageUrl') || '',
      };
      setCurrentMagazineStatus(get(generatedMagazineData, 'magazine.status', ''));
      setMagazineStatusKnown(true);
      setMagazineStatusError(false);
      setMagazine(updatedMagazineData);
      const isStatusDone = generatedMagazineData?.status === 'DONE';
      if (isStatusDone) {
        setRefetchGeneratedMagazine(false);
      }
      return false;
    },
    onError: (error) => {
      setMagazineStatusKnown(true);
      setMagazineStatusError(error?.response?.status !== 404);
    },
  });

  const generatedMagazineInfo = Array.isArray(toFormat) ? toFormat[0] : toFormat;
  const isMagazineSentToPrinting = isAdminEditing()
    ? false
    : currentMagazineStatus === rmMagazineStatus.SENT_FOR_PRINTING;
  // An unreadable status must not offer Publish for a magazine that may be sent.
  const isPublishBlocked = isMagazineSentToPrinting || (!isAdminEditing() && isMagazineStatusError);

  // Publish: start a non-preview regeneration (DS never prints a preview).
  const startPublishGeneration = async () => {
    const generate = isAdminEditing() ? generateReferralMagazineAdmin : generateReferralMagazine;
    const response = await generate({ payload: params, isPreview: false });
    return generatedMagazineIdFrom(response);
  };

  const goToPublishedMagazine = (generatedMagazine) => {
    notification.success({
      description: savedMessage,
      autoClose: 4000,
    });

    if (isAdminEditing()) {
      return history.push(
        getRouteRmMagazinePreview(generatedMagazine.id || params.previewId, params.customerId),
      );
    }

    return history.push(ROUTE_REFERRAL_MARKETING_GENERATED_MAGAZINE, {
      magazineId: presentMagazineID,
      params: {
        month: monthsList[get(generatedMagazine, 'magazine.month')],
        year: get(generatedMagazine, 'magazine.year'),
      },
    });
  };

  // Publish sends to print only the render it started, once DS would print it.
  const {
    phase: publishPhase,
    publish: publishWhenGenerated,
    forgetPendingGeneration,
  } = usePublishWhenGenerated({
    startGeneration: startPublishGeneration,
    fetchGenerated: async () =>
      firstGeneratedMagazine(
        await getGeneratedMagazineFn({ queryKey: getGeneratedMagazineQueryKey }),
      ),
    sendToPrint: async (generatedMagazine, signal) => {
      await sendToPrinting({ ...params, id: generatedMagazine.id });
      // The member may have left the editor while the print request ran.
      if (!signal.aborted) goToPublishedMagazine(generatedMagazine);
    },
    notify: (description) => notification.warning({ description, autoClose: 8000 }),
  });

  // Magazine data coming from strapi.
  const {
    isLoading: isStepsInfoFetching,
    data: magazinePageData = {},
    isSuccess: hasMagazinePageData,
  } = useQuery(['getMagazineStepsInfo', params.month, params.year], getMagazineStepsInfo, {
    keepPreviousData: true,
    onSuccess: (data) => {
      const {
        frontCoverDesign,
        frontCoverStrip,
        backCover,
        frontInsideCover,
        backInsideCover,
        pdf,
        previewPdf,
      } = data || {};

      const optionsDefaultValue = {
        backInsideCover: backInsideCover?.[0],
        backCover: backCover?.[0],
        frontInsideCover: frontInsideCover?.[0],
        frontCoverDesign: frontCoverDesign?.[0],
        frontCoverStrip: frontCoverStrip?.[0],
        pdf,
        previewPdf,
        maxCoverLetterLength: 1500,
      };
      return setMagazine({ ...magazine, ...optionsDefaultValue });
    },
  });

  let getMagazineQueryKey = ['getMagazine', params.year, params.month];
  let getMagazineFn = () => getMagazine(params.year, params.month);

  if (isAdminEditing()) {
    getMagazineQueryKey = ['getMagazineAdmin', params.year, params.month, params.customerId];
    getMagazineFn = getMagazineAdmin;
  }

  const {
    data: magazineFromDatabase = {},
    refetch: refetchMagazineFromDb,
    isFetched: isMagazineFromDatabaseFetched,
  } = useQuery(getMagazineQueryKey, getMagazineFn, {
    enabled: hasMagazinePageData,
    keepPreviousData: true,
    retry: retryUnlessNotFound,
    // Each answer resets the form to the saved magazine, so it is fetched only
    // when the editor opens and after the preview generation it starts, never
    // in the background while the member is typing. Saves sync the form from
    // their own answers instead.
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    onSuccess: async (data) => {
      if (!data) {
        const payload = {
          magazineId: magazinePageData?.id,
        };
        return createMagazine({ payload });
      }

      const hasValues = Object.keys(data || {})?.length;
      setPresentMagazineID(data?.id);
      const hasSameValues = isEqual(data?.id, get(magazineFromDatabase, 'id'));
      if (hasValues) {
        //  Check if we have data till backCover option
        const selections = get(data, 'selections');

        const hasBackCoverValue =
          selections.find((step) => step.formKeyword.includes('backCover'))?.formKeyword ?? false;

        if (!generatedMagazineInfo?.id && hasBackCoverValue) {
          if (!hasSameValues) {
            await generateMagazine({ payload: params, isPreview: true });
            fetchGeneratedMagazine();
            refetchMagazineFromDb();
            return false;
          }
        }
        if (!generatedMagazineInfo?.id) redirectUserToNextStep(data);
        // eslint-disable-next-line no-use-before-define
        syncFormikWithLastMagazineData(data);
        setPreviousMagazineData(data);
      }
      return true;
    },
  });

  const { frontCoverDesign, frontCoverStrip, backCover, frontInsideCover, backInsideCover } =
    magazinePageData || {};

  const { mutate: updateMagazine, isLoading: isUpdatingMagazine } = useMutation(
    (req) => {
      if (req?.payload?.selection?.formKeyword === 'frontInsideCover-option-1') {
        req.payload.selection.dynamicFields = replaceFinalPayload(req);
      }
      if (isAdminEditing()) {
        return updateReferralMagazineAdmin(req);
      }
      return updateReferralMagazine(req);
    },
    {
      onSuccess: async (data, req) => {
        // The next publish must render this edit, not resume an older render.
        forgetPendingGeneration();
        // Only step 1's own save refills step 1. `req.step` is the step the
        // member saved (`current` when they submitted).
        syncFormikWithLastMagazineData(data, { withPersonalInfoStep: req?.step === 0 });
        setPreviousMagazineData(data);

        if (current === 4) {
          await generateMagazine({ payload: params, isPreview: true });
        }
        notification.success({
          description: savedMessage,
          autoClose: 4000,
        });
        return setFormikContext('current', current + 1);
      },
      onError: () => {
        notification.error({
          description: 'Something went wrong',
          autoClose: 4000,
        });
      },
    },
  );

  // Initial api call to create magazine draft from step 1.
  const { mutate: createMagazine, isLoading: isCreatingMagazine } = useMutation(
    (payload) => createReferralMagazine(payload),
    {
      onSuccess: (data) => {
        trackUsage('magazine', 'create', { magazineId: data?.id });
        setPresentMagazineID(data.id);
        // eslint-disable-next-line no-use-before-define
        magazineDataFormik.setFieldValue('baseReplacers', savedPersonalInfoFrom(data));
        notification.success({
          description: savedMessage,
          autoClose: 4000,
        });
        return setFormikContext('current', current + 1);
      },
    },
  );

  /* eslint no-underscore-dangle: ['error', { 'allow': ['_formFields', '_id'] }] */
  const magazineDataFormik = useFormik({
    initialValues,
    validationSchema: magazineEditorValidationSchema(),
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async () => {
      const stepName = magazineFormikStepsList[current];
      const payload = {
        magazineId: magazinePageData.id,
      };
      const generatedMagzineExists = presentMagazineID || generatedMagazineInfo?.id;
      const location = 'frontInsideCover'.concat('.', '_formFields', '.', 'frontInsideCoverText');
      const data = replaceNewLineWithBR(
        magazineDataFormik.values.frontInsideCover._formFields.frontInsideCoverText,
      );
      await setFieldValue(location, data);

      const { baseReplacers: formikBaseReplacers = {} } = magazineDataFormik.values;

      validateStepFormFields();

      if (current === 0) {
        // `baseReplacers` in the form changes only with DS's answer.
        payload.baseReplacers = magazineDataFormik.values[stepName]._formFields;

        const actionToPerform = generatedMagzineExists ? updateMagazine : createMagazine;
        const req = {
          payload,
          step: current,
          ...(generatedMagzineExists && { month: params.month }),
          ...(generatedMagzineExists && { year: params.year }),
          ...(generatedMagzineExists && { customerId: params.customerId }),
        };
        return actionToPerform(req);
      }
      const selectedOption = magazine[stepName] || {};

      // `PATCH /generated-magazines/:id` is DS's lead-covers route: it saved
      // none of the links or additional information and appended an empty
      // `leadCovers` row, so the last step no longer calls it.
      if (current === 5) {
        payload[stepName] = magazine[stepName] ?? '';
        payload.isPreview = false;
      }

      payload.selection = {
        page: current,
        formKeyword: selectedOption?.formKeyword ?? stepName,
      };

      if (stepHavingForms.includes(current)) {
        const currentStepFormDataFields = magazineDataFormik.values[stepName]._formFields || {};
        payload.selection.dynamicFields = commonFunctions.convertObjectToArrayOfObject(
          currentStepFormDataFields,
        );
        const hasValues = Object.keys(formikBaseReplacers)?.length;
        if (hasValues) {
          payload.baseReplacers = formikBaseReplacers;
        }
      }

      if (current === 1) {
        const htmlReplacer = (magazine.frontCoverStrip?.formKeyword ?? '').split('-')[0];
        payload.selection.formKeyword = magazine.frontCoverDesign?.formKeyword;
        payload.selection.extraHtml = {
          formKeyword: magazine.frontCoverStrip?.formKeyword,
          dynamicFields: [...payload.selection.dynamicFields],
          htmlReplacer,
        };
        payload.selection.dynamicFields = [];
      }
      return updateMagazine({ ...params, payload, step: current });
    },
  });

  const { setFieldValue } = magazineDataFormik;
  // A save's callbacks are the ones of the render that started it; this holds
  // the form values as they are when DS answers. It is assigned during render,
  // not in an effect: an effect runs only after its commit, so it would lag one
  // commit behind the values a callback can already see. It is read only in
  // callbacks, never during render.
  const latestFormValues = useRef(magazineDataFormik.values);
  latestFormValues.current = magazineDataFormik.values;

  const updateFormikWithPreviousData = (formattedValues, formKeyword, page) => {
    const lastValue = {};

    const currentStep = magazineFormikStepsList[page];
    const formKeywordText = formKeyword.split('-');
    const keyFromMagazine = formKeywordList[formKeywordText[0]];
    const currentStepMagazineData = magazinePageData[keyFromMagazine] || [];
    lastValue[keyFromMagazine] = currentStepMagazineData.find(
      (option) => option.formKeyword === formKeyword,
    );
    const valueToInitialize = {
      [currentStep]: {
        _formFields: formattedValues,
      },
    };
    return { lastValue, valueToInitialize };
  };

  // Step 1 is filled from the saved `baseReplacers[0]` here when the editor
  // opens and after step 1's own save (`withPersonalInfoStep`): Formik copies
  // `initialValues` when it mounts, so the saved fields reach the form only
  // through this reset. Other saves leave step 1's unsaved typing alone.
  const syncFormikWithLastMagazineData = (
    lastMagazineData,
    { withPersonalInfoStep = true } = {},
  ) => {
    const filledSteps = lastMagazineData.selections;

    let magazineData = {};
    const { resetForm } = magazineDataFormik;
    let reInitializeFormikValues = {};

    for (let index = 0; index < filledSteps.length; index++) {
      const stepInfo = filledSteps[index];
      const { dynamicFields = [], formKeyword, page, extraHtml = {} } = stepInfo;
      const formattedValues = getFormattedValues(dynamicFields);
      const {
        lastValue,
        valueToInitialize: stepValueToReInitialize,
      } = updateFormikWithPreviousData(formattedValues, formKeyword, page);
      reInitializeFormikValues = {
        ...reInitializeFormikValues,
        ...stepValueToReInitialize,
      };
      magazineData = { ...magazineData, ...lastValue };
      const hasExtraInfo = Object.keys(extraHtml)?.length;
      if (hasExtraInfo) {
        const {
          dynamicFields: extraDynamicFields = [],
          formKeyword: extraInfoFormKeyword,
        } = extraHtml;
        const extraValues = getFormattedValues(extraDynamicFields);
        const {
          lastValue: previousValue,
          valueToInitialize: extraValueToReInitialize,
        } = updateFormikWithPreviousData(extraValues, extraInfoFormKeyword, page);
        reInitializeFormikValues = {
          ...reInitializeFormikValues,
          ...extraValueToReInitialize,
        };
        magazineData = { ...magazineData, ...previousValue };
      }
    }
    // `baseReplacers` is always DS's saved copy, never the form's.
    const savedPersonalInfo = savedPersonalInfoFrom(lastMagazineData);
    const fillPersonalInfoStep = withPersonalInfoStep && !isEmpty(savedPersonalInfo);
    reInitializeFormikValues = {
      ...reInitializeFormikValues,
      ...(fillPersonalInfoStep && { magazine: { _formFields: { ...savedPersonalInfo } } }),
      baseReplacers: savedPersonalInfo,
    };

    resetForm({
      values: { ...latestFormValues.current, ...reInitializeFormikValues },
    });
    return setMagazine((latestMagazine) => ({ ...latestMagazine, ...magazineData }));
  };

  const handleImageLightBox = (imageForLightBox) => {
    setShowImageLightBox(!showImageLightBox);
    setImageLightBox({
      src: imageForLightBox,
      alt: 'Magazine option',
      loading: 'lazy',
    });
  };

  const handleUpdateMagazine = (newValue, keyToUpdate) => {
    const latestMagazineData = { ...magazine, [keyToUpdate]: newValue };
    setMagazine(latestMagazineData);
    setFieldValue('selectedOption', newValue.formKeyword);
  };

  useEffect(() => {
    setFieldValue('currentStep', current);
  }, [current, setFieldValue, isMagazineSentToPrinting, setFormikContext]);

  useEffect(() => {
    if (stepError >= 0) {
      setFormikContext('current', stepError);
      setPublishedError(true);
    }
  }, [setFormikContext, stepError]);

  // Reset to the first step only when leaving the editor: a cleanup on the
  // effect above would also run when an error clears, overriding the step the
  // member chose.
  useEffect(() => () => setFormikContext('current', 0), [setFormikContext]);

  const goToNextStep = () => {
    return setFormikContext('current', current + 1);
  };

  const goToPreviousStep = () => {
    return setFormikContext('current', current - 1);
  };

  const onPublishMagazine = () =>
    publishWhenGenerated(async () => {
      const hasError = await validateStepFormFields();

      if (hasError) return false;

      const location = 'frontInsideCover'.concat('.', '_formFields', '.', 'frontInsideCoverText');
      const data = replaceNewLineWithBR(
        magazineDataFormik.values.frontInsideCover._formFields.frontInsideCoverText,
      );
      await setFieldValue(location, data);

      setFormikContext('current', 5);
      return true;
    });

  const validateStepFormFields = async (step = 0) => {
    const currentStep = current === -1 ? 0 : current;
    const stepName = magazineFormikStepsList[currentStep];
    const { validateForm } = magazineDataFormik || {};

    const hasError = await validateForm().then((err) => {
      if (Object.prototype.hasOwnProperty.call(err, stepName)) {
        setStepError(currentStep);
        return true;
      }
      return false;
    });

    if (!hasError) {
      setStepSucceed((prevStep) => [...prevStep, current]);
      setFormikContext('current', step);
      setStepError(-1);
      setPublishedError(false);
    } else {
      setPublishedError(hasError);
      setStepSucceed((prevStep) => prevStep.filter((stp) => stp !== current));
    }

    return hasError;
  };

  const renderCurrentStep = (step) => {
    const nextStepExists = rmMagzineEditorSteps.length;
    const buttonText = current === 5 ? 'Generate Magazine' : 'Next Step';
    const isMagazineAlreadyPrinted = isAdminEditing()
      ? false
      : get(magazineFromDatabase, '[0].status') === rmMagazineStatus.SENT_FOR_PRINTING;
    switch (step) {
      case 0:
        return (
          <MagazineStepPage
            step="magazine"
            goToNextStep={goToNextStep}
            updateMagazineData={{
              magazine,
              handleUpdateMagazine,
              magazineDataFormik,
              createMagazine,
              setFormikContext,
            }}
            disabled={isMagazineAlreadyPrinted}
            params={params}
            previewUrl={previewUrl}
          />
        );
      case 1:
        return (
          <FrontCoverStepPage
            handleImageLightBox={handleImageLightBox}
            updateMagazineData={{
              magazine,
              handleUpdateMagazine,
              magazineDataFormik,
              updateMagazine,
              previousMagazineData,
              setFormikContext,
            }}
            goToNextStep={goToNextStep}
            goToPreviousStep={goToPreviousStep}
            frontCoverDesign={frontCoverDesign}
            frontCoverStrip={frontCoverStrip}
          />
        );
      case 2:
        return (
          <FrontInsideCover
            updateMagazineData={{
              magazine,
              handleUpdateMagazine,
              magazineDataFormik,
              previousMagazineData,
              setFormikContext,
            }}
            frontInsideCover={frontInsideCover}
            goToNextStep={goToNextStep}
            goToPreviousStep={goToPreviousStep}
          />
        );
      case 3:
        return (
          <BackInsideCover
            handleImageLightBox={handleImageLightBox}
            updateMagazineData={{
              magazine,
              handleUpdateMagazine,
              magazineDataFormik,
              previousMagazineData,
              setFormikContext,
            }}
            backInsideCover={backInsideCover}
            goToNextStep={goToNextStep}
            goToPreviousStep={goToPreviousStep}
          />
        );
      case 4:
        return (
          <BackCoverStepPage
            handleImageLightBox={handleImageLightBox}
            updateMagazineData={{
              magazine,
              handleUpdateMagazine,
              magazineDataFormik,
              previousMagazineData,
              setFormikContext,
            }}
            goToNextStep={goToNextStep}
            goToPreviousStep={goToPreviousStep}
            backCoverList={backCover}
          />
        );
      case 5:
        return (
          <GenerateMagazineStepPage
            generatedMagazineInfo={generatedMagazineInfo}
            updateMagazineData={{
              magazine,
              handleUpdateMagazine,
              magazineDataFormik,
              setFormikContext,
            }}
            goToNextStep={goToNextStep}
            goToPreviousStep={goToPreviousStep}
          />
        );
      default:
        return (
          <S.CurrentStep>
            You are on {rmMagzineEditorSteps[step]} step.
            {current < nextStepExists && (
              <S.BottomWrap>
                <PrimaryButton onClick={goToNextStep}>{buttonText}</PrimaryButton>
              </S.BottomWrap>
            )}
          </S.CurrentStep>
        );
    }
  };

  // The Publish button and the banner depend on the magazine status, and the
  // saved magazine sets the current step and form data, so the editor stays
  // behind the loader until both have answered.
  const isLoadingMagazine =
    isStepsInfoFetching ||
    !isMagazineStatusKnown ||
    (hasMagazinePageData && !isMagazineFromDatabaseFetched);
  const isPublishing = isCreatingMagazine || isUpdatingMagazine || publishPhase !== 'idle';
  const showLoader = isLoadingMagazine || isPublishing;

  const publishLoaderDetail = {
    generating: magazinePublishStrings.generating,
    sending: magazinePublishStrings.sending,
  };

  if (!showLoader && !magazinePageData?.id) {
    return (
      <div className="relative w-full h-full min-h-[630px] overflow-hidden">
        <div className="w-full h-full flex flex-col justify-center bg-white rounded-lg p-6 shadow-box">
          <div className="frame_280 flex flex-col justify-center items-center self-stretch gap-4">
            <div className="frame_824 flex flex-col justify-center items-center self-stretch">
              <Alert className="frame_191 flex flex-col items-center self-stretch">
                <div className="self-stretch text-neutral-800 text-center  text-base font-semibold">
                  No active magazine for this month of
                  <span className="capitalize">{params.month}</span> {params.year}
                </div>
                <div className="text-neutral-600 text-centertext-sm font-medium leading-[1.4375rem]">
                  You haven't ordered any magazines yet
                </div>
              </Alert>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showLoader) {
    return (
      <Loader
        height="75vh"
        title={isPublishing ? 'Publishing your magazine...' : magazinePublishStrings.loading}
        description={
          isPublishing
            ? publishLoaderDetail[publishPhase] || 'Personalizing your digital magazine'
            : magazinePublishStrings.loadingDetail
        }
      />
    );
  }

  const onLightboxClose = () => setShowImageLightBox(false);
  const lightBoxStyles = { background: 'rgba(0,0,0,0.5)' };

  const magazinePreview = previewUrl ?? magazine?.previewPdf;

  return (
    <div className="flex flex-col w-full font-figtree">
      <S.SubPageContainer width="100%">
        <>
          <div className="w-full flex justify-between items-center self-stretch flex-wrap max-[700px]:justify-center">
            <div className="text-neutral-800 text-2xl font-bold">RM Magazine editor</div>
            <div className="flex items-center flex-wrap justify-center gap-2">
              <Button
                buttonType="button"
                onClick={() => setOpenModal(true)}
                size="lg"
                color="light"
              >
                See sample preview
              </Button>
              <Button
                buttonType="button"
                disabled={isPublishBlocked}
                onClick={() => onPublishMagazine()}
                size="lg"
                type="primary"
              >
                <PublishBookIcon />
                Publish magazine
              </Button>
            </div>
          </div>
          <div>
            {isPublishedError && (
              <Alert color="failure" className="my-6">
                <div className="w-full flex gap-4 items-center">
                  <AlertWarning />
                  <span className="font-medium text-error-500">
                    Please complete all required steps to publish your magazine
                  </span>
                </div>
              </Alert>
            )}
            {isAdminEditing() && (
              <Alert color="info" className="my-6 flex ga-3">
                <div className="w-full flex gap-4 items-center">
                  <InfoIconNew fill="#0e7490" />
                  <span className="font-medium text-info-500">
                    You are editing the magazine of <span className="capitalize">{fullName}.</span>
                    <a href={getRouteRmMagazineReport()}>Back to report</a>
                  </span>
                </div>
              </Alert>
            )}
            {!isAdminEditing() && isMagazineStatusKnown && !isMagazineStatusError && (
              <Alert color="warning" className="bg-secondary-orange-50 my-6">
                <div className="w-full flex gap-4 items-center">
                  <AlertWarning />
                  <span className="font-medium text-notificationWarning">
                    {isMagazineSentToPrinting
                      ? "Your magazine was sent to print. Changes made now won't be printed. If you need a change, contact"
                      : 'When everything looks good, click “Publish magazine” to send it to print. After that, changes can only be made by'}
                    <a
                      href="https://authorify.com/contact-us/"
                      target="_blank"
                      rel="noreferrer"
                      className="font-bold text-notificationWarning hover:text-notificationWarning underline pl-1"
                    >
                      support
                    </a>
                    .
                  </span>
                </div>
              </Alert>
            )}
          </div>
          <div className="mt-6 rounded-lg bg-white border-neutral-300 shadow-box">
            <div className="flex items-center gap-6 self-stretch pt-[1.375rem] pb-[1.375rem] px-6 border-b border-b-neutral-200">
              <div className="frame_787 flex flex-col justify-center items-start">
                <div className="text-boxTitle font-semibold capitalize">{`${params.month} ${params.year} Magazine`}</div>
                <div className="text-neutral-500 text-sm font-medium">
                  The information provided here will be used for the magazine. Customization options
                  available in these steps are free of charge.
                </div>
              </div>
            </div>
            {/** Steps */}
            <div className="flex flex-col items-start self-stretch px-0">
              {stepsList.map(({ step, title }) => (
                <div
                  key={title}
                  className="flex flex-col items-center self-stretch px-6 py-5 border-b"
                >
                  <div key={title} className="flex items-center gap-5 self-stretch">
                    {isMagazineSentToPrinting && (
                      <>
                        {isPublishedError && step === stepError ? (
                          <AlertSolidWarningIcon />
                        ) : (
                          <div className="bg-success-500 rounded-full p-1.5">
                            <StepCheckIcon />
                          </div>
                        )}
                      </>
                    )}
                    {!isMagazineSentToPrinting && isPublishedError && (
                      <>{step === stepError && <AlertSolidWarningIcon />}</>
                    )}
                    {!isMagazineSentToPrinting && (
                      <>
                        {stepSucceed.includes(step) ? (
                          <div className="bg-success-500 rounded-full p-1.5">
                            <StepCheckIcon />
                          </div>
                        ) : (
                          <>
                            {stepError !== step && (
                              <div
                                className={`step_${step} flex justify-center items-center w-[1.875rem] h-[1.875rem] rounded-full border-2 ${
                                  current === step
                                    ? 'bg-neutral-800 text-white border-neutral-800'
                                    : 'bg-none text-neutral-400 border-neutral-400'
                                }`}
                              >
                                <div className="flex flex-col flex-shrink-0 p-0.5 justify-center items-center gap-2.5 text-sm font-semibold">
                                  {step + 1}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                    <div className="frame_792-1 w-full flex justify-between items-center">
                      <div
                        className={`${
                          current === step ? 'text-neutral-800' : 'text-neutral-400'
                        } text-base font-semibold`}
                      >
                        {title}
                      </div>
                      <button
                        color="light"
                        onClick={() => {
                          validateStepFormFields(step);
                        }}
                        type="button"
                        className={`${
                          current === step ? 'hidden' : 'text-neutral-800'
                        } flex w-[110px] justify-center items-center border text-neutral-800 px-[18px] py-[13px] rounded-md border-solid border-neutral-200 hover:border-neutral-500 hover:bg-neutral-50`}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                  <div className="w-full pl-[50px]">
                    {current === step && renderCurrentStep(current)}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/** Modal */}
          <Modal
            dismissible
            show={openModal}
            onClose={() => setOpenModal(false)}
            size="6xl"
            theme={{
              body: {
                base: 'h-[631px] p-6',
              },
              sizes: {
                '6xl': 'max-w-6xl',
              },
            }}
          >
            <div>
              <div className="flex justify-between items-center self-stretch p-6">
                <div className="text-neutral-800 font-bold">
                  <span className="capitalize mr-[2px]">
                    {params.month} {params.year}
                  </span>
                  sample magazine
                </div>
                <Button
                  onClick={() => setOpenModal(false)}
                  type="button"
                  color="light"
                  className="flex justify-center items-center gap-2 p-2.5 p-2 w-9 h-9 rounded-md bg-neutral-100 focus:ring-0"
                >
                  <CloseXIcon />
                </Button>
              </div>
              <div className="flex items-start gap-3 self-stretch py-2 px-3 rounded-md bg-secondary-orange-50 mx-6">
                <AlertWarning />
                <div className="content flex flex-col items-start gap-2">
                  <div className="list flex flex-col items-start self-stretch">
                    <div className="alerts_list_item flex items-start self-stretch text text-secondary-orange-600 text-sm font-semibold">
                      This preview is a sample only and NOT your finalized version. If all inputted
                      information is correct, our setup team will send a proof link via your
                      registered email with the accurate magazine preview within 24-48 business
                      hours upon receiving the order
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Modal.Body>
              {magazinePreview ? (
                <afy-flip-book key="magazine-step-pdf" pdfurl={magazinePreview} />
              ) : null}
            </Modal.Body>
          </Modal>
        </>
      </S.SubPageContainer>
      <Lightbox
        isOpen={showImageLightBox}
        images={[imageLightBox]}
        // As we are passing array wth 1 image only
        currentIndex={0}
        style={lightBoxStyles}
        onClose={onLightboxClose}
      />
    </div>
  );
};

// Another month (or member) is another magazine: remount the editor so no
// form value or state of the previous one carries over.
const MagazineEditorForParams = (props) => {
  const { month, year, customerId } = useParams();
  return <MagazineEditor key={[month, year, customerId].join('/')} {...props} />;
};

export default MagazineEditorForParams;
